const diacriticMap: Record<'゛' | '゜', Record<string, string>> = {
  '゛': {
    か: 'が', き: 'ぎ', く: 'ぐ', け: 'げ', こ: 'ご',
    さ: 'ざ', し: 'じ', す: 'ず', せ: 'ぜ', そ: 'ぞ',
    た: 'だ', ち: 'ぢ', つ: 'づ', て: 'で', と: 'ど',
    は: 'ば', ひ: 'び', ふ: 'ぶ', へ: 'べ', ほ: 'ぼ',
    カ: 'ガ', キ: 'ギ', ク: 'グ', ケ: 'ゲ', コ: 'ゴ',
    サ: 'ザ', シ: 'ジ', ス: 'ズ', セ: 'ゼ', ソ: 'ゾ',
    タ: 'ダ', チ: 'ヂ', ツ: 'ヅ', テ: 'デ', ト: 'ド',
    ハ: 'バ', ヒ: 'ビ', フ: 'ブ', ヘ: 'ベ', ホ: 'ボ',
  },
  '゜': {
    は: 'ぱ', ひ: 'ぴ', ふ: 'ぷ', へ: 'ぺ', ほ: 'ぽ',
    ハ: 'パ', ヒ: 'ピ', フ: 'プ', ヘ: 'ペ', ホ: 'ポ',
  },
};

const smallKanaMap: Record<string, string> = {
  あ: 'ぁ', い: 'ぃ', う: 'ぅ', え: 'ぇ', お: 'ぉ',
  つ: 'っ', や: 'ゃ', ゆ: 'ゅ', よ: 'ょ', わ: 'ゎ',
  ア: 'ァ', イ: 'ィ', ウ: 'ゥ', エ: 'ェ', オ: 'ォ',
  ツ: 'ッ', ヤ: 'ャ', ユ: 'ュ', ヨ: 'ョ', ワ: 'ヮ',
};

/**
 * Build the full cycle for each base kana.
 * Examples:
 *   は → ば → ぱ → は  (dakuten + handakuten)
 *   か → が → か        (dakuten only)
 *   つ → づ → っ → つ   (dakuten + small)
 *   あ → ぁ → あ        (small only)
 */
const buildCycleMap = (): Record<string, string> => {
  const cycleMap: Record<string, string> = {};
  const dakuten = diacriticMap['゛'];
  const handakuten = diacriticMap['゜'];

  // All base kana that have at least one variant
  const allBases = new Set([
    ...Object.keys(dakuten),
    ...Object.keys(handakuten),
    ...Object.keys(smallKanaMap),
  ]);

  allBases.forEach(base => {
    const chain: string[] = [base];

    if (dakuten[base]) chain.push(dakuten[base]);
    if (handakuten[base]) chain.push(handakuten[base]);
    if (smallKanaMap[base]) chain.push(smallKanaMap[base]);

    // Link each step to the next, last loops back to first
    for (let i = 0; i < chain.length; i++) {
      cycleMap[chain[i]] = chain[(i + 1) % chain.length];
    }
  });

  return cycleMap;
};

const cycleMap = buildCycleMap();

/**
 * Get the next character in the diacritic/small cycle.
 * Returns undefined if the character has no cycle.
 */
export const getNextCycleCharacter = (input: string): string | undefined => {
  const lastChar = input.at(-1);
  if (lastChar && cycleMap[lastChar]) {
    return cycleMap[lastChar];
  }
};

export const getDiacriticCharacter = (input: string, mark: '゛' | '゜'): string | undefined => {
  const lastChar = input.at(-1);
  if (lastChar) {
    const map = diacriticMap[mark];

    // If already a diacritic, revert to base
    const baseEntry = Object.entries(map).find(([, v]) => v === lastChar);
    if (baseEntry) {
      const [base] = baseEntry;
      return base;
    }

    // If matching kana, apply diacritic
    if (map[lastChar]) {
      return map[lastChar];
    }
  }
};

export const modifiers = {
  '゛': getDiacriticCharacter,
  '゜': getDiacriticCharacter,
};